import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  buildChannelOptions,
  buildPanelLabels,
  buildPreferenceRows,
  Icons,
  NotificationPreferencesPanel,
  SUPPORTED_TIMEZONES,
  type PreferenceChannelKey,
  useSnackbar,
} from '@munifocus/ui';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '../../lib/notifications-api';
import { SettingsSection as Section } from './SettingsSection';

/**
 * The Notifications card on the Officer Portal settings screen.
 *
 * Every change saves immediately rather than behind a Save button. These are
 * single-value switches with an obvious effect and a visible saving state; a
 * form that has to be submitted invites an officer to flip three toggles, walk
 * away, and be surprised at 2am that none of them took.
 */
export function NotificationsSection() {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const preferences = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();

  const save = (patch: Parameters<typeof update.mutate>[0]) =>
    update.mutate(patch, {
      onSuccess: () => snackbar.success(t('common.notifications.settings.saved')),
      onError: () => snackbar.error(t('common.notifications.settings.error')),
    });

  const data = preferences.data;

  return (
    <Section
      icon={Icons.Bell}
      title={t('common.notifications.settings.title')}
      description={t('common.notifications.settings.description')}
    >
      {/* A failed load used to leave the panel spinning forever, since "no data"
          is also what loading looks like. Say so, and offer the retry. */}
      {preferences.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void preferences.refetch()}>
              {t('common.actions.retry')}
            </Button>
          }
        >
          {t('common.notifications.settings.loadError')}
        </Alert>
      ) : (
        <NotificationPreferencesPanel
          loading={preferences.isLoading || !data}
          saving={update.isPending}
          categories={data ? buildPreferenceRows(data, t) : []}
          channels={buildChannelOptions(t)}
          digest={data?.digest ?? 'immediate'}
          quietHours={data?.quietHours ?? null}
          timezone={data?.timezone ?? SUPPORTED_TIMEZONES[0]}
          timezones={SUPPORTED_TIMEZONES}
          labels={buildPanelLabels(t)}
          onToggle={(categoryId: string, channel: PreferenceChannelKey, next: boolean) =>
            save({ categories: { [categoryId]: { [channel]: next } } })
          }
          onDigestChange={(digest) => save({ digest })}
          onQuietHoursChange={(quietHours) => save({ quietHours })}
          onTimezoneChange={(timezone) => save({ timezone })}
        />
      )}
    </Section>
  );
}
